import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MarkerType,
  ReactFlowProvider,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  fetchBoards,
  createBoard,
  deleteBoard,
  fetchBoardGraph,
  fetchBoardConnections,
  createConnection,
  patchConnection,
  updateBoardItem,
  deleteBoardItem,
  generateWhiteboardInsights,
  getWhiteboardInsights,
} from '../services/dashboardService';
import { usePinToBoard } from '../contexts/PinToBoardContext';
import '../styles/Whiteboard.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const ITEM_TYPE_COLORS = {
  intelligence:   '#f59e0b',
  knowledge_node: '#6366f1',
  pdf:            '#ef4444',
  news:           '#0ea5e9',
  market_data:    '#10b981',
  community:      '#8b5cf6',
  custom:         '#64748b',
};

const CONNECTION_TYPE_COLORS = {
  auto_kg:             '#6366f1',
  auto_semantic:       '#10b981',
  auto_entity_overlap: '#f59e0b',
  manual:              '#2563eb',
};

const CONNECTION_TYPE_LABELS = {
  auto_kg:             'KG',
  auto_semantic:       'SEM',
  auto_entity_overlap: 'ENT',
  manual:              'MAN',
};

const CONN_FILTER_OPTIONS = [
  { value: 'all',                label: 'All' },
  { value: 'auto_kg',            label: 'KG' },
  { value: 'auto_semantic',      label: 'Semantic' },
  { value: 'auto_entity_overlap',label: 'Entity' },
  { value: 'manual',             label: 'Manual' },
];

const itemColor  = (type) => ITEM_TYPE_COLORS[type]  || '#94a3b8';
const connColor  = (type) => CONNECTION_TYPE_COLORS[type] || '#94a3b8';
const connLabel  = (type) => CONNECTION_TYPE_LABELS[type] || type;

// ─────────────────────────────────────────────────────────────────────────────
// Graph helpers — convert API payload → ReactFlow elements
// ─────────────────────────────────────────────────────────────────────────────

const apiNodesToRF = (apiNodes) =>
  (apiNodes || []).map((n) => ({
    id: String(n.id),
    position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
    data: {
      label: n.label,
      itemType: n.type,
      refId: n.ref_id,
      color: n.color,
      metadata: n.metadata || {},
      tags: n.tags || [],
      content: n.content,
    },
    style: {
      background: n.color || itemColor(n.type),
      color: '#fff',
      border: '2px solid rgba(255,255,255,0.25)',
      borderRadius: 10,
      padding: '8px 14px',
      fontSize: 12,
      fontWeight: 600,
      minWidth: 110,
      maxWidth: 200,
      boxShadow: '0 3px 10px rgba(0,0,0,0.22)',
      cursor: 'pointer',
      wordBreak: 'break-word',
      textAlign: 'center',
    },
  }));

const apiEdgesToRF = (apiEdges) =>
  (apiEdges || [])
    .filter((e) => !e.is_hidden)
    .map((e) => {
      const isManual = e.connection_type === 'manual';
      const color = connColor(e.connection_type);
      return {
        id: String(e.id),
        source: String(e.source),
        target: String(e.target),
        label: e.label || '',
        data: {
          connectionType: e.connection_type,
          confidenceScore: e.confidence_score,
          evidence: e.evidence,
          isHidden: e.is_hidden,
        },
        type: 'default',
        animated: false,
        style: {
          stroke: color,
          strokeWidth: isManual ? 2.5 : 1.8,
          strokeDasharray: isManual ? undefined : '5,4',
        },
        markerEnd: { type: MarkerType.ArrowClosed, color },
        labelStyle: {
          fill: '#1e293b',
          fontWeight: 600,
          fontSize: 10,
        },
        labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.88, rx: 3, ry: 3 },
        labelBgPadding: [4, 6],
      };
    });

// ─────────────────────────────────────────────────────────────────────────────
// Confidence bar helper
// ─────────────────────────────────────────────────────────────────────────────

const ConfBar = ({ score }) => {
  if (score == null) return null;
  const pct = Math.round(score * 100);
  const cls = score >= 0.7 ? 'wb-conf-high' : score >= 0.4 ? 'wb-conf-mid' : 'wb-conf-low';
  return (
    <div className="wb-conn-confidence">
      <div className="wb-conf-bar">
        <div className={`wb-conf-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="wb-conf-label">{pct}%</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Toast helper
// ─────────────────────────────────────────────────────────────────────────────

const Toast = ({ msg, type }) => (
  <div className={`wb-toast ${type || ''}`}>
    <i className={`fas fa-${type === 'success' ? 'check-circle' : type === 'error' ? 'exclamation-circle' : 'info-circle'}`} />
    {msg}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Metadata value renderer — handles nested objects, arrays, primitives
// ─────────────────────────────────────────────────────────────────────────────

const MetaValue = ({ value }) => {
  if (value == null) return <span className="wb-meta-val wb-meta-null">—</span>;

  if (typeof value === 'boolean') {
    return (
      <span className="wb-meta-val">
        <span className={`wb-meta-bool ${value ? 'wb-meta-bool-yes' : 'wb-meta-bool-no'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      </span>
    );
  }

  if (typeof value === 'number') {
    return <span className="wb-meta-val">{value.toLocaleString()}</span>;
  }

  if (typeof value === 'string') {
    return <span className="wb-meta-val">{value}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="wb-meta-val wb-meta-null">(empty)</span>;
    const allPrimitive = value.every(
      (item) => item == null || typeof item !== 'object',
    );
    if (allPrimitive) {
      return (
        <div className="wb-meta-val wb-meta-tags">
          {value.map((item, i) => (
            <span key={i} className="wb-meta-tag">{item == null ? '—' : String(item)}</span>
          ))}
        </div>
      );
    }
    return (
      <pre className="wb-meta-val wb-meta-json">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="wb-meta-val wb-meta-null">(empty)</span>;
    const isFlat = entries.every(([, v]) => v == null || typeof v !== 'object');
    if (isFlat) {
      return (
        <div className="wb-meta-sub-table">
          {entries.map(([k, v]) => (
            <div key={k} className="wb-meta-sub-row">
              <span className="wb-meta-sub-key">{k}</span>
              <span className="wb-meta-sub-val">{v == null ? '—' : String(v)}</span>
            </div>
          ))}
        </div>
      );
    }
    return (
      <pre className="wb-meta-val wb-meta-json">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return <span className="wb-meta-val">{String(value)}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Item Detail Drawer
// ─────────────────────────────────────────────────────────────────────────────

const ItemDrawer = ({ item, connections, onClose }) => {
  if (!item) return null;

  const { data } = item;
  const relatedConns = (connections || []).filter(
    (c) => c.source_item_id === item.id || c.target_item_id === item.id,
  );

  const metaEntries = Object.entries(data.metadata || {}).filter(
    ([, v]) => v != null && v !== '',
  );

  return (
    <div className="wb-item-drawer">
      {/* Header */}
      <div className="wb-drawer-header">
        <div className="wb-drawer-title-block">
          <span
            className="wb-drawer-type-chip"
            style={{ background: itemColor(data.itemType) }}
          >
            {data.itemType}
          </span>
          <h3 className="wb-drawer-title">{data.label}</h3>
        </div>
        <button className="wb-drawer-close-btn" onClick={onClose} title="Close">
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Tags */}
      {data.tags && data.tags.length > 0 && (
        <div className="wb-drawer-section">
          <p className="wb-drawer-section-label">
            <i className="fas fa-tags" /> Tags
          </p>
          <div className="wb-tags">
            {data.tags.map((t) => (
              <span key={t} className="wb-tag">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Annotation */}
      {data.content && (
        <div className="wb-drawer-section">
          <p className="wb-drawer-section-label">
            <i className="fas fa-pencil-alt" /> Annotation
          </p>
          <p className="wb-drawer-content-text">{data.content}</p>
        </div>
      )}

      {/* Metadata */}
      {metaEntries.length > 0 && (
        <div className="wb-drawer-section">
          <p className="wb-drawer-section-label">
            <i className="fas fa-database" /> Metadata
          </p>
          <div className="wb-meta-table">
            {metaEntries.map(([k, v]) => (
              <div className="wb-meta-row" key={k}>
                <span className="wb-meta-key">{k}</span>
                <MetaValue value={v} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connections */}
      {relatedConns.length > 0 && (
        <div className="wb-drawer-section">
          <p className="wb-drawer-section-label">
            <i className="fas fa-link" /> Connections ({relatedConns.length})
          </p>
          {relatedConns.map((c) => {
            const otherTitle =
              c.source_item_id === item.id ? c.target_title : c.source_title;
            const score = c.confidence_score;
            const pct = score != null ? Math.round(score * 100) : null;
            const chipColor = connColor(c.connection_type);
            return (
              <div key={c.id} className="wb-drawer-conn-row">
                <span
                  className="wb-conn-type-chip"
                  style={{ background: chipColor }}
                >
                  {connLabel(c.connection_type)}
                </span>
                <span className="wb-drawer-conn-name" title={otherTitle}>
                  {otherTitle || '—'}
                </span>
                {pct != null && (
                  <span
                    className="wb-drawer-conf"
                    style={{
                      background: score >= 0.7 ? 'rgba(16,185,129,0.15)' : score >= 0.4 ? 'rgba(245,158,11,0.15)' : 'rgba(148,163,184,0.15)',
                      color: score >= 0.7 ? '#15803d' : score >= 0.4 ? '#a16207' : '#64748b',
                    }}
                  >
                    {pct}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Canvas inner (ReactFlow instance)
// ─────────────────────────────────────────────────────────────────────────────

const CanvasInner = ({
  initialNodes,
  initialEdges,
  onNodeClick,
  onEdgesConnect,
  onNodeDragStop,
  highlightNodeId,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => { setNodes(initialNodes); }, [initialNodes, setNodes]);
  useEffect(() => { setEdges(initialEdges); }, [initialEdges, setEdges]);

  const handleConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
      if (onEdgesConnect) onEdgesConnect(params);
    },
    [setEdges, onEdgesConnect],
  );

  // Apply highlight ring to selected node
  const styledNodes = nodes.map((n) => ({
    ...n,
    style: {
      ...n.style,
      outline: n.id === highlightNodeId ? '3px solid #facc15' : 'none',
      outlineOffset: '2px',
      boxShadow:
        n.id === highlightNodeId
          ? '0 0 0 4px rgba(250,204,21,0.35), 0 4px 14px rgba(0,0,0,0.28)'
          : '0 3px 10px rgba(0,0,0,0.22)',
      zIndex: n.id === highlightNodeId ? 10 : 1,
    },
  }));

  return (
    <ReactFlow
      nodes={styledNodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={handleConnect}
      onNodeClick={(_, node) => onNodeClick && onNodeClick(node)}
      onNodeDragStop={(_, node) => onNodeDragStop && onNodeDragStop(node)}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      minZoom={0.15}
      maxZoom={2.5}
      deleteKeyCode={null}
      nodesDraggable
      nodesConnectable
      elementsSelectable
    >
      <Background color="#cbd5e1" gap={20} size={1} />
      <Controls />
    </ReactFlow>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Confirm Dialog
// ─────────────────────────────────────────────────────────────────────────────

const ConfirmDialog = ({ title, message, onConfirm, onCancel }) => (
  <div className="wb-confirm-overlay" onClick={onCancel}>
    <div className="wb-confirm-box" onClick={(e) => e.stopPropagation()}>
      <h4>{title}</h4>
      <p>{message}</p>
      <div className="wb-confirm-actions">
        <button className="wb-confirm-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button className="wb-confirm-delete-btn" onClick={onConfirm}>
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Whiteboard Insights Panel
// ─────────────────────────────────────────────────────────────────────────────

const InsightListGroup = ({ icon, label, items, itemClass }) => {
  if (!items?.length) return null;
  return (
    <div className="wb-insights-group">
      <p className="wb-insights-group-label"><i className={icon} /> {label}</p>
      <ul className="wb-insights-list">
        {items.map((item, i) => (
          <li key={i} className={`wb-insights-list-item ${itemClass || ''}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const WhiteboardInsightsPanel = ({ data, loading, error, onClose, onRegenerate }) => {
  const insight = data?.insight;

  return (
    <div className="wb-insights-panel">
      {/* Header */}
      <div className="wb-insights-header">
        <div className="wb-insights-header-left">
          <i className="fas fa-brain wb-insights-brain-icon" />
          <div>
            <h3 className="wb-insights-title">Board Insights</h3>
            {data?.created_at && (
              <span className="wb-insights-timestamp">
                Generated {new Date(data.created_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="wb-insights-header-actions">
          <button
            className="wb-insights-regen-btn"
            onClick={onRegenerate}
            title="Regenerate insights"
            disabled={loading}
          >
            <i className={`fas fa-sync-alt${loading ? ' fa-spin' : ''}`} />
          </button>
          <button className="wb-insights-close-btn" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="wb-insights-loading">
          <div className="wb-spinner" />
          <p>Analysing board…</p>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="wb-insights-error">
          <i className="fas fa-exclamation-triangle" />
          <p>{error}</p>
          <button onClick={onRegenerate}>Retry</button>
        </div>
      )}

      {/* Results */}
      {!loading && !error && insight && (
        <div className="wb-insights-body">

          {/* ── Flat format: { summary, highlights, recommendations } ── */}
          {/* This is the format currently stored in the DB               */}

          {/* Top-level summary */}
          {insight.summary && (
            <div className="wb-insights-section">
              <h4 className="wb-insights-section-title">
                <i className="fas fa-chart-pie" /> Summary
              </h4>
              <p className="wb-insights-overview">{insight.summary}</p>
            </div>
          )}

          {/* Highlights */}
          {insight.highlights?.length > 0 && (
            <div className="wb-insights-section">
              <h4 className="wb-insights-section-title">
                <i className="fas fa-list-ul" /> Highlights
              </h4>
              <ul className="wb-insights-list">
                {insight.highlights.map((h, i) => (
                  <li key={i} className="wb-insights-list-item wb-insights-theme">{h}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Structured format: { board_summary, connection_analysis, … } ── */}
          {/* Retained for backward compat if/when new format is used            */}

          {/* Board Summary */}
          {insight.board_summary && (
            <div className="wb-insights-section">
              <h4 className="wb-insights-section-title">
                <i className="fas fa-chart-pie" /> Board Summary
              </h4>
              {insight.board_summary.overview && (
                <p className="wb-insights-overview">{insight.board_summary.overview}</p>
              )}
              <InsightListGroup icon="fas fa-tags"   label="Themes"         items={insight.board_summary.themes}         itemClass="wb-insights-theme"    />
              <InsightListGroup icon="fas fa-signal" label="Market Signals" items={insight.board_summary.market_signals} itemClass="wb-insights-signal"   />
              <InsightListGroup icon="fas fa-exclamation-triangle" label="Anomalies" items={insight.board_summary.anomalies} itemClass="wb-insights-anomaly" />
            </div>
          )}

          {/* Connection Analysis */}
          {insight.connection_analysis && (
            <div className="wb-insights-section">
              <h4 className="wb-insights-section-title">
                <i className="fas fa-project-diagram" /> Connection Analysis
              </h4>
              {insight.connection_analysis.overview && (
                <p className="wb-insights-overview">{insight.connection_analysis.overview}</p>
              )}
              <InsightListGroup icon="fas fa-link"   label="Key Relationships"   items={insight.connection_analysis.key_relationships}   itemClass="wb-insights-relationship" />
              <InsightListGroup icon="fas fa-star"   label="Hub Items"           items={insight.connection_analysis.hub_items}            itemClass="wb-insights-hub"          />
              <InsightListGroup icon="fas fa-search" label="Investigation Leads" items={insight.connection_analysis.investigation_leads} itemClass="wb-insights-lead"         />
            </div>
          )}

          {/* Recommendations (shared by both formats) */}
          {insight.recommendations?.length > 0 && (
            <div className="wb-insights-section">
              <h4 className="wb-insights-section-title">
                <i className="fas fa-lightbulb" /> Recommendations
              </h4>
              <ul className="wb-insights-list">
                {insight.recommendations.map((r, i) => (
                  <li key={i} className="wb-insights-list-item wb-insights-recommendation">
                    <span className="wb-insights-rec-num">{i + 1}</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer meta */}
          <div className="wb-insights-footer-row">
            {data?.created_at && (
              <span className="wb-insights-footer-meta">
                <i className="fas fa-clock" /> {new Date(data.created_at).toLocaleString()}
              </span>
            )}
            {data?.tokens_used != null && (
              <span className="wb-insights-footer-meta">
                <i className="fas fa-microchip" /> {data.tokens_used} tokens
              </span>
            )}
          </div>
        </div>
      )}

      {/* Empty — panel open but no data yet and not loading */}
      {!loading && !error && !insight && (
        <div className="wb-insights-loading">
          <i className="fas fa-brain" style={{ fontSize: '2.5rem', color: 'var(--accent)', opacity: 0.35 }} />
          <p style={{ marginTop: 8 }}>No insights generated yet.</p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main WhiteboardPage
// ─────────────────────────────────────────────────────────────────────────────

const WhiteboardPage = ({ initialBoardId } = {}) => {
  // ── Board list ──────────────────────────────────────────────────────────────
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [activeBoardId, setActiveBoardId] = useState(null);

  // ── New board creation ──────────────────────────────────────────────────────
  const [showNewBoardInput, setShowNewBoardInput] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const newBoardInputRef = useRef(null);

  // ── Canvas & graph ──────────────────────────────────────────────────────────
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState(null);
  const [rfNodes, setRfNodes] = useState([]);
  const [rfEdges, setRfEdges] = useState([]);
  const [graphStats, setGraphStats] = useState(null);

  // ── Connections sidebar ─────────────────────────────────────────────────────
  const [connections, setConnections] = useState([]);
  const [connFilter, setConnFilter] = useState('all');

  // ── Dirty position tracking (for Save Layout) ───────────────────────────────
  const dirtyPositions = useRef({});   // itemId → { position_x, position_y }
  const [hasDirty, setHasDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Item drawer ─────────────────────────────────────────────────────────────
  const [selectedNode, setSelectedNode] = useState(null);

  // ── Confirm dialog ──────────────────────────────────────────────────────────
  const [confirmState, setConfirmState] = useState(null); // { type, payload }

  // ── PinToBoard context — keep pin popover in sync with board deletions ───────
  const { removeBoard: removeBoardFromPinCtx } = usePinToBoard();

  // ── Toast ───────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // ── Board Insights ──────────────────────────────────────────────────────────
  const [showInsightsPanel, setShowInsightsPanel] = useState(false);
  const [insightsData, setInsightsData]           = useState(null);
  const [insightsLoading, setInsightsLoading]     = useState(false);
  const [insightsError, setInsightsError]         = useState(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const showToast = useCallback((msg, type = '') => {
    clearTimeout(toastTimerRef.current);
    setToast({ msg, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId) || null,
    [boards, activeBoardId],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Load boards on mount
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      setBoardsLoading(true);
      const res = await fetchBoards();
      if (res.success) {
        const list = res.data || [];
        setBoards(list);
        // Auto-select default or first board
        const def = list.find((b) => b.is_default) || list[0];
        if (def) setActiveBoardId(def.id);
      }
      setBoardsLoading(false);
    })();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Deep-link: jump to a specific board if initialBoardId is provided
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!initialBoardId || boardsLoading) return;
    if (boards.some((b) => b.id === initialBoardId)) {
      setActiveBoardId(initialBoardId);
    }
  }, [initialBoardId, boards, boardsLoading]);

  // ─────────────────────────────────────────────────────────────────────────
  // Load graph when active board changes
  // ─────────────────────────────────────────────────────────────────────────

  // Reset insights when switching boards
  useEffect(() => {
    setInsightsData(null);
    setInsightsError(null);
    setShowInsightsPanel(false);
  }, [activeBoardId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeBoardId) return;
    dirtyPositions.current = {};
    setHasDirty(false);
    setSelectedNode(null);
    setRfNodes([]);
    setRfEdges([]);
    setConnections([]);
    setGraphError(null);

    const load = async () => {
      setGraphLoading(true);
      const [graphRes, connRes] = await Promise.all([
        fetchBoardGraph(activeBoardId),
        fetchBoardConnections(activeBoardId),
      ]);

      if (graphRes.success) {
        setRfNodes(apiNodesToRF(graphRes.data?.nodes));
        setRfEdges(apiEdgesToRF(graphRes.data?.edges));
        setGraphStats(graphRes.stats || null);
      } else {
        setGraphError(graphRes.message || 'Failed to load board.');
      }

      if (connRes.success) {
        setConnections(connRes.data || []);
      }

      setGraphLoading(false);
    };

    load();
  }, [activeBoardId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Focus new board input when shown
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (showNewBoardInput && newBoardInputRef.current) {
      newBoardInputRef.current.focus();
    }
  }, [showNewBoardInput]);

  // ─────────────────────────────────────────────────────────────────────────
  // Create board
  // ─────────────────────────────────────────────────────────────────────────

  const handleCreateBoard = useCallback(async () => {
    const name = newBoardName.trim();
    if (!name) return;
    setCreatingBoard(true);
    const res = await createBoard({ name });
    setCreatingBoard(false);
    if (res.success) {
      const newBoard = res.data;
      setBoards((prev) => [...prev, newBoard]);
      setActiveBoardId(newBoard.id);
      setNewBoardName('');
      setShowNewBoardInput(false);
      showToast(`Board "${newBoard.name}" created`, 'success');
    } else {
      showToast(res.message || 'Failed to create board', 'error');
    }
  }, [newBoardName, showToast]);

  const handleNewBoardKeyDown = (e) => {
    if (e.key === 'Enter') handleCreateBoard();
    if (e.key === 'Escape') {
      setShowNewBoardInput(false);
      setNewBoardName('');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Delete board
  // ─────────────────────────────────────────────────────────────────────────

  const handleDeleteBoard = useCallback(() => {
    if (!activeBoard) return;
    setConfirmState({
      type: 'delete-board',
      payload: activeBoard,
    });
  }, [activeBoard]);

  const confirmDeleteBoard = useCallback(async () => {
    const board = confirmState?.payload;
    if (!board) return;
    setConfirmState(null);
    const res = await deleteBoard(board.id);
    if (res.success) {
      const remaining = boards.filter((b) => b.id !== board.id);
      setBoards(remaining);
      setActiveBoardId(remaining[0]?.id || null);
      // Keep the pin-to-board popover board list in sync
      removeBoardFromPinCtx(board.id);
      showToast(`Board "${board.name}" deleted`, 'success');
    } else {
      showToast(res.message || 'Failed to delete board', 'error');
    }
  }, [boards, confirmState, removeBoardFromPinCtx, showToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // Node drag-stop → buffer dirty positions
  // ─────────────────────────────────────────────────────────────────────────

  const handleNodeDragStop = useCallback((node) => {
    dirtyPositions.current[node.id] = {
      position_x: node.position.x,
      position_y: node.position.y,
    };
    setHasDirty(true);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Save layout — batch PATCH all dirty positions
  // ─────────────────────────────────────────────────────────────────────────

  const handleSaveLayout = useCallback(async () => {
    if (!activeBoardId || !hasDirty) return;
    setSaving(true);

    // Only save positions for items that still exist on the board,
    // and that have valid numeric coordinates (guards against NaN/undefined).
    const activeIds = new Set(rfNodes.map((n) => n.id));
    const entries = Object.entries(dirtyPositions.current).filter(
      ([itemId, pos]) =>
        activeIds.has(itemId) &&
        typeof pos.position_x === 'number' && !Number.isNaN(pos.position_x) &&
        typeof pos.position_y === 'number' && !Number.isNaN(pos.position_y),
    );

    if (entries.length === 0) {
      dirtyPositions.current = {};
      setHasDirty(false);
      setSaving(false);
      showToast('Layout saved', 'success');
      return;
    }

    const results = await Promise.all(
      entries.map(([itemId, pos]) =>
        updateBoardItem(activeBoardId, itemId, {
          position_x: pos.position_x,
          position_y: pos.position_y,
        }),
      ),
    );
    setSaving(false);
    const failed = results.filter((r) => !r.success);
    if (failed.length === 0) {
      dirtyPositions.current = {};
      setHasDirty(false);
      showToast('Layout saved', 'success');
    } else {
      // Log first failure for debugging
      console.error('[Save Layout] failed items:', failed.map((r) => r.message));
      showToast(`${failed.length} position(s) failed to save`, 'error');
    }
  }, [activeBoardId, hasDirty, rfNodes, showToast]);

  // ─────────────────────────────────────────────────────────────────────────
  // Node click → open drawer
  // ─────────────────────────────────────────────────────────────────────────

  const handleNodeClick = useCallback((node) => {
    setSelectedNode((prev) => (prev?.id === node.id ? null : node));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Manual connection from React Flow onConnect
  // ─────────────────────────────────────────────────────────────────────────

  const handleConnect = useCallback(
    async (params) => {
      if (!activeBoardId) return;
      const res = await createConnection(activeBoardId, {
        source_item_id: params.source,
        target_item_id: params.target,
        connection_type: 'manual',
      });
      if (res.success) {
        setConnections((prev) => [...prev, res.data]);
        showToast('Manual connection created', 'success');
      } else {
        showToast(res.message || 'Failed to create connection', 'error');
      }
    },
    [activeBoardId, showToast],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Dismiss connection
  // ─────────────────────────────────────────────────────────────────────────

  const handleDismissConnection = useCallback(
    async (conn) => {
      if (!activeBoardId) return;
      const res = await patchConnection(activeBoardId, conn.id, { is_hidden: true });
      if (res.success) {
        setConnections((prev) => prev.filter((c) => c.id !== conn.id));
        // Remove edge from canvas
        setRfEdges((prev) => prev.filter((e) => e.id !== conn.id));
        showToast('Connection dismissed', '');
      } else {
        showToast(res.message || 'Failed to dismiss connection', 'error');
      }
    },
    [activeBoardId, showToast],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Remove item from board
  // ─────────────────────────────────────────────────────────────────────────

  const handleRemoveItem = useCallback(
    async (nodeId) => {
      if (!activeBoardId) return;
      const res = await deleteBoardItem(activeBoardId, nodeId);
      if (res.success) {
        setRfNodes((prev) => prev.filter((n) => n.id !== nodeId));
        setRfEdges((prev) =>
          prev.filter((e) => e.source !== nodeId && e.target !== nodeId),
        );
        setConnections((prev) =>
          prev.filter(
            (c) => c.source_item_id !== nodeId && c.target_item_id !== nodeId,
          ),
        );
        if (selectedNode?.id === nodeId) setSelectedNode(null);
        // Clear any buffered dirty position for the removed node
        delete dirtyPositions.current[nodeId];
        if (Object.keys(dirtyPositions.current).length === 0) setHasDirty(false);
        // Update board count in the list
        setBoards((prev) =>
          prev.map((b) =>
            b.id === activeBoardId
              ? { ...b, item_count: Math.max(0, (b.item_count || 1) - 1) }
              : b,
          ),
        );
        showToast('Item removed from board', '');
      } else {
        showToast(res.message || 'Failed to remove item', 'error');
      }
    },
    [activeBoardId, selectedNode, showToast],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Board Insights — open panel, fetch saved or generate new
  // ─────────────────────────────────────────────────────────────────────────

  const handleOpenInsights = useCallback(async (forceGenerate = false) => {
    setShowInsightsPanel(true);
    if (!activeBoardId) return;

    // If we already have data and are not forcing a regenerate, just show it
    if (insightsData && !forceGenerate) return;

    setInsightsLoading(true);
    setInsightsError(null);

    if (!forceGenerate) {
      // Try to load the most recent saved insight for this board first
      const savedRes = await getWhiteboardInsights(activeBoardId, { limit: 1 });
      if (savedRes.success && savedRes.data?.length > 0) {
        setInsightsData(savedRes.data[0]);
        setInsightsLoading(false);
        return;
      }
    }

    // Generate a fresh insight
    const genRes = await generateWhiteboardInsights(activeBoardId);
    if (genRes.success) {
      setInsightsData(genRes.data);
    } else {
      setInsightsError(genRes.message || 'Failed to generate insights.');
    }
    setInsightsLoading(false);
  }, [activeBoardId, insightsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────
  // Filtered connections for sidebar
  // ─────────────────────────────────────────────────────────────────────────

  const filteredConns = useMemo(
    () =>
      connFilter === 'all'
        ? connections
        : connections.filter((c) => c.connection_type === connFilter),
    [connections, connFilter],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Sidebar item list from rfNodes
  // ─────────────────────────────────────────────────────────────────────────

  const sidebarItems = useMemo(
    () =>
      rfNodes.map((n) => ({
        id: n.id,
        title: n.data.label,
        itemType: n.data.itemType,
      })),
    [rfNodes],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  if (boardsLoading) {
    return (
      <div className="wb-page">
        <div className="wb-state-center" style={{ height: '100%' }}>
          <div className="wb-spinner" />
          <p>Loading boards…</p>
        </div>
      </div>
    );
  }

  // ── No boards at all ──
  if (!boardsLoading && boards.length === 0) {
    return (
      <div className="wb-page">
        <div className="wb-no-boards">
          <i className="fas fa-chalkboard" />
          <h3>No boards yet</h3>
          <p>
            Create your first investigation board to start pinning intelligence,
            documents, and research notes.
          </p>
          <button
            className="wb-create-first-btn"
            onClick={() => setShowNewBoardInput(true)}
          >
            <i className="fas fa-plus" /> New Board
          </button>
          {showNewBoardInput && (
            <div className="wb-new-board-input-wrap" style={{ marginTop: 8 }}>
              <input
                ref={newBoardInputRef}
                className="wb-new-board-input"
                placeholder="Board name…"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                onKeyDown={handleNewBoardKeyDown}
                disabled={creatingBoard}
              />
              <button
                className="wb-new-board-confirm-btn"
                onClick={handleCreateBoard}
                disabled={creatingBoard || !newBoardName.trim()}
              >
                {creatingBoard ? '…' : 'Create'}
              </button>
              <button
                className="wb-new-board-cancel-btn"
                onClick={() => {
                  setShowNewBoardInput(false);
                  setNewBoardName('');
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          )}
        </div>
        {toast && <Toast msg={toast.msg} type={toast.type} />}
      </div>
    );
  }

  // ── Main layout ──
  return (
    <div className="wb-page">
      {/* ─── Board selector bar ─── */}
      <div className="wb-board-bar">
        {boards.map((b) => (
          <button
            key={b.id}
            className={`wb-board-tab ${activeBoardId === b.id ? 'active' : ''}`}
            onClick={() => setActiveBoardId(b.id)}
            title={b.description || b.name}
          >
            <span
              className="wb-board-tab-dot"
              style={{
                background:
                  activeBoardId === b.id
                    ? 'rgba(255,255,255,0.7)'
                    : (b.color || '#94a3b8'),
              }}
            />
            {b.name}
            <span className="wb-board-tab-count">
              ({b.item_count ?? 0})
            </span>
          </button>
        ))}

        <div className="wb-board-bar-divider" />

        {/* Save layout button */}
        <button
          className={`wb-board-action-btn wb-save-btn${hasDirty ? ' has-dirty' : ''}`}
          onClick={handleSaveLayout}
          disabled={saving || !hasDirty}
          title={hasDirty ? 'Save canvas positions' : 'No unsaved positions'}
        >
          <i className={`fas fa-${saving ? 'spinner fa-spin' : 'save'}`} />
          Save Layout
        </button>

        {/* Insights button */}
        <button
          className={`wb-board-action-btn wb-insights-btn${showInsightsPanel ? ' active' : ''}`}
          onClick={() => showInsightsPanel ? setShowInsightsPanel(false) : handleOpenInsights()}
          title="Board Analytics Insights"
          disabled={!activeBoardId}
        >
          <i className={`fas fa-brain${insightsLoading ? ' fa-fade' : ''}`} />
          Insights
        </button>

        {/* New board */}
        {showNewBoardInput ? (
          <div className="wb-new-board-input-wrap">
            <input
              ref={newBoardInputRef}
              className="wb-new-board-input"
              placeholder="Board name…"
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              onKeyDown={handleNewBoardKeyDown}
              disabled={creatingBoard}
            />
            <button
              className="wb-new-board-confirm-btn"
              onClick={handleCreateBoard}
              disabled={creatingBoard || !newBoardName.trim()}
            >
              {creatingBoard ? '…' : 'Create'}
            </button>
            <button
              className="wb-new-board-cancel-btn"
              onClick={() => {
                setShowNewBoardInput(false);
                setNewBoardName('');
              }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        ) : (
          <button
            className="wb-board-action-btn wb-new-board-btn"
            onClick={() => setShowNewBoardInput(true)}
            title="Create a new board"
          >
            <i className="fas fa-plus" /> New Board
          </button>
        )}
      </div>

      {/* ─── Workspace (sidebar + canvas) ─── */}
      <div className="wb-workspace">
        {/* ── Sidebar ── */}
        <div className="wb-sidebar">
          {/* Board meta */}
          {activeBoard && (
            <div className="wb-board-meta">
              <h3 className="wb-board-meta-name">
                <span
                  className="wb-board-color-dot"
                  style={{ background: activeBoard.color || '#94a3b8' }}
                />
                {activeBoard.name}
              </h3>
              <div className="wb-board-meta-stats">
                <span className="wb-board-meta-stat">
                  <i className="fas fa-thumbtack" />
                  {activeBoard.item_count ?? rfNodes.length} items
                </span>
                <span className="wb-board-meta-stat">
                  <i className="fas fa-link" />
                  {graphStats?.visible_edges ?? filteredConns.length} connections
                </span>
              </div>
              <button
                className="wb-board-delete-btn"
                onClick={handleDeleteBoard}
                title="Delete this board"
              >
                <i className="fas fa-trash-alt" /> Delete board
              </button>
            </div>
          )}

          {/* Connections section */}
          <div className="wb-sidebar-section" style={{ flex: '0 0 auto', maxHeight: '48%' }}>
            <div className="wb-sidebar-section-header">
              <span className="wb-sidebar-section-title">
                <i className="fas fa-link" /> Connections
              </span>
              <span className="wb-sidebar-section-count">{filteredConns.length}</span>
            </div>

            {/* Type filter pills */}
            <div className="wb-conn-filter-row">
              {CONN_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`wb-conn-filter-btn ${connFilter === opt.value ? 'active' : ''}`}
                  onClick={() => setConnFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="wb-sidebar-list">
              {filteredConns.length === 0 ? (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', padding: '16px 0' }}>
                  No connections
                </p>
              ) : (
                filteredConns.map((conn) => (
                  <div key={conn.id} className="wb-conn-row">
                    <span
                      className="wb-conn-type-chip"
                      style={{ background: connColor(conn.connection_type) }}
                    >
                      {connLabel(conn.connection_type)}
                    </span>
                    <div className="wb-conn-names">
                      <span className="wb-conn-label" title={conn.relationship_label}>
                        {conn.relationship_label || '—'}
                      </span>
                      <span className="wb-conn-pair">
                        {conn.source_title} → {conn.target_title}
                      </span>
                    </div>
                    <ConfBar score={conn.confidence_score} />
                    <button
                      className="wb-conn-dismiss-btn"
                      title="Dismiss connection"
                      onClick={() => handleDismissConnection(conn)}
                    >
                      <i className="fas fa-eye-slash" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Items section */}
          <div className="wb-sidebar-section">
            <div className="wb-sidebar-section-header">
              <span className="wb-sidebar-section-title">
                <i className="fas fa-thumbtack" /> Pinned Items
              </span>
              <span className="wb-sidebar-section-count">{sidebarItems.length}</span>
            </div>
            <div className="wb-sidebar-list">
              {sidebarItems.length === 0 ? (
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', padding: '16px 0' }}>
                  No items pinned
                </p>
              ) : (
                sidebarItems.map((item) => (
                  <div
                    key={item.id}
                    className={`wb-item-row ${selectedNode?.id === item.id ? 'selected' : ''}`}
                    onClick={() => {
                      const node = rfNodes.find((n) => n.id === item.id);
                      if (node) handleNodeClick(node);
                    }}
                  >
                    <span
                      className="wb-item-type-chip"
                      style={{ background: itemColor(item.itemType) }}
                    >
                      {item.itemType}
                    </span>
                    <span className="wb-item-title" title={item.title}>
                      {item.title}
                    </span>
                    <button
                      className="wb-item-remove-btn"
                      title="Remove from board"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(item.id);
                      }}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Canvas ── */}
        <div className="wb-canvas-wrap">
          {graphLoading && (
            <div className="wb-state-center">
              <div className="wb-spinner" />
              <p>Loading board…</p>
            </div>
          )}

          {!graphLoading && graphError && (
            <div className="wb-state-center">
              <i className="wb-state-icon fas fa-exclamation-circle" style={{ color: '#ef4444', opacity: 1 }} />
              <h3>Failed to load board</h3>
              <p>{graphError}</p>
            </div>
          )}

          {!graphLoading && !graphError && (
            <>
              {rfNodes.length === 0 && (
                <div className="wb-canvas-empty-overlay">
                  <i className="fas fa-chalkboard" />
                  <p>Pin items here using the <i className="fas fa-thumbtack" style={{ color: '#f59e0b' }} /> button on any listing, document, news article or knowledge node</p>
                </div>
              )}
              <ReactFlowProvider>
                <CanvasInner
                  initialNodes={rfNodes}
                  initialEdges={rfEdges}
                  onNodeClick={handleNodeClick}
                  onEdgesConnect={handleConnect}
                  onNodeDragStop={handleNodeDragStop}
                  highlightNodeId={selectedNode?.id}
                />
              </ReactFlowProvider>

              {/* Item detail drawer — overlaid on canvas */}
              {selectedNode && !showInsightsPanel && (
                <ItemDrawer
                  item={selectedNode}
                  connections={connections}
                  onClose={() => setSelectedNode(null)}
                />
              )}

              {/* Board Insights panel — overlaid on canvas */}
              {showInsightsPanel && (
                <WhiteboardInsightsPanel
                  data={insightsData}
                  loading={insightsLoading}
                  error={insightsError}
                  onClose={() => setShowInsightsPanel(false)}
                  onRegenerate={() => handleOpenInsights(true)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── Confirm dialog ─── */}
      {confirmState?.type === 'delete-board' && (
        <ConfirmDialog
          title="Delete board?"
          message={`This will permanently delete "${confirmState.payload.name}" and all its items and connections.`}
          onConfirm={confirmDeleteBoard}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {/* ─── Toast ─── */}
      {toast && <Toast msg={toast.msg} type={toast.type} />}
    </div>
  );
};

export default WhiteboardPage;
